import { GameTypeId, GameTypeIdType } from '@/enums/gameTypeId';
import { RootState } from '@/lib/store';
import { handleDifficulty } from '@/lib/store/games-preferences/actions';
import { GameDifficulty } from '@/models/game/difficulty';
import { MinesGameDifficulty } from '@/models/mines/game-difficulty';
import { MissionUncrossableGameDifficulty } from '@/models/mission-uncrossable/game-difficulty';
import { TowerGameDifficulty } from '@/models/tower/game-difficulty';
import { usePaginatedQuery } from '@starsoft/common/hooks';
import { GenericError, PaginatedResponse } from '@starsoft/common/models';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GameDifficultiesQueryKey } from '../../queryKey';
import {
  GameDifficultyProps,
  UseGameDifficultiesProps,
  UseGameDifficultiesQueryKey,
  UseGameDifficultiesReturnProps,
} from './props';
import { fetchGameDifficulties } from './service';

export function useGameDifficulties<T extends GameTypeIdType>({
  gameTypeId,
  enabled = true,
}: UseGameDifficultiesProps<T>): UseGameDifficultiesReturnProps<T> {
  const dispatch = useDispatch();
  const [page, setPage] = useState<number>(1);
  const mines = useSelector((state: RootState) => state.mines.mineQuantity);
  const difficultyId = useSelector(
    (state: RootState) => state.gamePreferences.difficultyId,
  );

  const queryKey: UseGameDifficultiesQueryKey = [
    GameDifficultiesQueryKey.Primary,
    {
      gameTypeId,
      page,
      limit: 50,
    },
  ];

  const { data, isPending, fetchStatus, ...infiniteQuery } = useQuery<
    PaginatedResponse<GameDifficultyProps<T>>,
    AxiosError<GenericError>,
    PaginatedResponse<GameDifficultyProps<T>>,
    UseGameDifficultiesQueryKey
  >({
    queryKey,
    enabled: enabled && !!gameTypeId,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
    queryFn: fetchGameDifficulties,
  });

  const paginatedCallbacks = usePaginatedQuery({
    data,
    page,
    setPage,
    queryParams: {
      gameTypeId,
    },
  });

  function handleSetDefaultDifficulty() {
    const typedData =
      data?.data as unknown as GameDifficultyProps<T>['gameDifficulties'];

    if (!typedData) {
      return;
    }

    switch (gameTypeId?.toString()) {
      case GameTypeId.Mines.toString():
        const minesDifficulty = typedData?.find(
          difficulty =>
            (difficulty as MinesGameDifficulty).minesCount === mines,
        ) as unknown as MinesGameDifficulty;
        dispatch(handleDifficulty(minesDifficulty?.difficultyId));
        break;
      case GameTypeId.Tower.toString():
        const towerDifficulty =
          typedData?.[0] as unknown as TowerGameDifficulty;
        dispatch(handleDifficulty(towerDifficulty?.difficultyId));
        break;
      case GameTypeId.MissionUncrossable.toString():
        const missionUncrossableDifficulty =
          typedData?.[0] as unknown as MissionUncrossableGameDifficulty;
        dispatch(handleDifficulty(missionUncrossableDifficulty?.difficultyId));
        break;
      default:
        break;
    }
  }

  useEffect(handleSetDefaultDifficulty, [data, gameTypeId, mines, dispatch]);

  const currentDifficulty = useMemo(() => {
    return data?.data?.find(difficulty => {
      if (difficulty instanceof GameDifficulty) {
        return difficulty.id === difficultyId;
      }

      return (difficulty as any).difficultyId === difficultyId;
    });
  }, [data, difficultyId]);

  return {
    ...infiniteQuery,
    gameDifficulties:
      data?.data as unknown as GameDifficultyProps<T>['gameDifficulties'],
    isPending: (isPending && fetchStatus !== 'idle') as boolean,
    ...paginatedCallbacks,
    page,
    currentDifficulty,
  } as unknown as UseGameDifficultiesReturnProps<T>;
}
